import React, { useEffect, useRef, useCallback } from 'react'
import get from 'lodash/get'
import PropTypes from 'prop-types'
import cx from 'classnames'
import { toast } from 'react-toastify'
import { Progress } from '@local/do-secundo-progress'
import { ErrorComponent } from '@local/do-secundo-error'
import { useInquireLoyalty } from '../use-loyalty'
import { useLoyaltyRedemption } from './use-loyalty-redemption'
import { Loading, LoadingVariant } from '@local/do-secundo-loading'
import { Dollars } from '@local/do-secundo-dollars'
import { Fieldset } from '@local/do-secundo-fieldset'

import Star from '../../../assets/icon-rewards-star.svg'

import styles from './LoyaltyRedemption.module.css'

export const LoyaltyRedemption = ({ cart }) => {
  const [inquireLoyalty, loyaltyInquiry] = useInquireLoyalty()

  useEffect(
    function inquireLoyaltyOnMount() {
      inquireLoyalty()
    },
    [inquireLoyalty]
  )

  const getInLoyaltyInquiry = useCallback(
    /**
     * @param {string[]|string} path
     * @param {*} [defaultValue]
     * @returns {*} - resolved return value from path
     */
    (path, defaultValue) => {
      const pathArg = Array.isArray(path)
        ? ['data', 'loyaltyInquiry'].concat(path)
        : `data.loyaltyInquiry.${path}`
      return get(loyaltyInquiry, pathArg, defaultValue)
    },
    [loyaltyInquiry]
  )

  /** Expect that first element in the redemptions array is our 'primary' rewards redemption */
  const redemption = getInLoyaltyInquiry(['redemptions', 0], null)

  const {
    addError,
    removeError,
    addRedemption,
    removeRedemption,
    redemptionGuidOnCart,
    addLoading,
    removeLoading,
    remainingBalance
  } = useLoyaltyRedemption({ cart, redemption })

  /** undefined means redemptionGuid not let loaded,
   * null means loaded and no loyalty redemption is available */
  const prevRedemptionGuidOnCart = useRef(undefined)

  useEffect(
    function trackPrevRedemptionGuidWhenLoaded() {
      if (
        loyaltyInquiry.data &&
        prevRedemptionGuidOnCart.current === undefined
      ) {
        prevRedemptionGuidOnCart.current = redemptionGuidOnCart
      }
    },
    [loyaltyInquiry.data, redemptionGuidOnCart]
  )

  useEffect(
    function toastWhenRedemptionOnCartChanges() {
      if (prevRedemptionGuidOnCart.current === undefined) return

      if (prevRedemptionGuidOnCart.current !== redemptionGuidOnCart) {
        const message = redemptionGuidOnCart
          ? 'Reward has been applied!'
          : 'Reward will be saved for later.'
        toast(message)
        prevRedemptionGuidOnCart.current = redemptionGuidOnCart
      }
    },
    [loyaltyInquiry.data, redemptionGuidOnCart]
  )

  if (loyaltyInquiry.loading) {
    return <Progress />
  }

  if (
    !loyaltyInquiry.called ||
    loyaltyInquiry.error ||
    !getInLoyaltyInquiry('loyaltyAccountIdentifier', null)
  ) {
    return null
  }

  if (!redemption) {
    return (
      <Fieldset label='Rewards' collapsable>
        <span>No available rewards to redeem</span>
      </Fieldset>
    )
  }

  const anyLoading = addLoading || removeLoading
  /** if removing a redemption, then inactive. Otherwise, active when loading or currently selected redemption */
  const isRedeemedSelected = redemptionGuidOnCart === redemption.redemptionGuid
  const redeemedIsActive = !removeLoading && (isRedeemedSelected || addLoading)

  return (
    <Fieldset label='Rewards' collapsable>
      <div className={styles.rewardText} data-testid='reward-text'>
        <Star />
        Nice! You have a{' '}
        <span className={styles.rewardValue}>
          <Dollars amount={redemption.redemptionAmount} truncateZeroCents />{' '}
          reward
        </span>
      </div>
      {addError && <ErrorComponent error={addError} />}
      {removeError && <ErrorComponent error={removeError} />}
      <div className={styles.rewardButtonGroup}>
        <button
          type='button'
          className={cx(styles.rewardButton, {
            /** If a selectedGuid is present, only active when remove loading, otherwise only active if not loading an addRedemption  */
            [styles.active]: redemptionGuidOnCart ? removeLoading : !addLoading
          })}
          data-testid='save-redemption-for-later'
          /** Disabled if any loading state, or selectedGuid is not present */
          disabled={anyLoading || !redemptionGuidOnCart}
          onClick={removeRedemption}
        >
          {removeLoading ? (
            <Loading variant={LoadingVariant.SECONDARY} centerDots />
          ) : (
            'Save for later'
          )}
        </button>
        <button
          type='button'
          className={cx(styles.rewardButton, {
            [styles.active]: redeemedIsActive
          })}
          data-testid='use-redemption-0'
          /** Disabled if any loading state, or selectedGuid matches this redemption */
          disabled={anyLoading || redeemedIsActive}
          onClick={addRedemption}
        >
          {addLoading ? (
            <Loading variant={LoadingVariant.SECONDARY} centerDots />
          ) : (
            'Redeem now'
          )}
        </button>
      </div>
      {Boolean(remainingBalance) && (
        <div
          className={styles.remainingBalanceText}
          data-testid='remaining-balance-text'
        >
          You have a <Dollars amount={remainingBalance} truncateZeroCents />{' '}
          reward remaining for next time.
        </div>
      )}
    </Fieldset>
  )
}

LoyaltyRedemption.propTypes = {
  cart: PropTypes.object.isRequired
}
